import { Box, Chip, Typography, alpha } from '@mui/material';
import React from 'react';
import { BoxContainer } from '../CreateShipment/styles';
import { BoxType } from '../../store/reducers/Shipment/Type';
import { RootStateType } from '../../store';
import { useSelector } from 'react-redux';
import { UNIT } from '../../services/calculateVolume';
import { customWeightRounding } from '../../utils/convertWeight';

interface PropsType {
  box: BoxType;
  setSelectedBox: (box: BoxType) => void;
  isSelected: boolean;
}

function BoxInfo({ value, label }: { label: string; value: string }) {
  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'baseline',
        gap: 1,
        mb: 0.75,
        width: '100%',
      }}
    >
      <Typography
        sx={{
          fontSize: '14px',
          fontWeight: 500,
          color: 'common.black',
          flexShrink: 0,
        }}
      >
        {label}
      </Typography>
      <Typography
        sx={{
          fontSize: '14px',
          fontWeight: 400,
          color: 'text.secondary',
          textAlign: 'right',
          wordBreak: 'break-word',
        }}
      >
        {value}
      </Typography>
    </Box>
  );
}

function SingleBoxView({ box, setSelectedBox, isSelected }: PropsType) {
  const { shipmentById } = useSelector((state: RootStateType) => state.shipment);
  const { chareableWeight } = useSelector((state: RootStateType) => state.getRates);
  const selectedUnit = shipmentById?._packageId?.unitOfMeasurement?.split('-')?.[0] || 'kg';
  const selectedLengthUnit: UNIT =
    shipmentById?._packageId?.unitOfMeasurement?.split('-')?.[1] === UNIT.cm ? UNIT.cm : UNIT.in;
  const { id, weight, length, width, height } = box;

  return (
    <BoxContainer
      container
      alignItems={'center'}
      className="rounded-lg"
      isselected={String(isSelected)}
      onClick={() => {
        setSelectedBox(box);
      }}
      sx={{ p: { xs: 1.5, sm: 2 }, mb: 1.5 }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', mb: 1.5 }}>
        <Typography sx={{ fontSize: '16px', fontWeight: 500, color: 'primary.main' }}>
          {`Box ${Number(id) + 1}`}
        </Typography>
        {isSelected && (
          <Chip
            label="Selected"
            size="small"
            sx={{
              height: 20,
              fontSize: '0.6875rem',
              fontWeight: 600,
              bgcolor: theme => alpha(theme.palette.primary.main, 0.1),
              color: 'primary.main',
            }}
          />
        )}
      </Box>

      <BoxInfo label="Actual Weight" value={`${weight || 0} ${selectedUnit}`} />
      <BoxInfo
        label="Volumetric Weight"
        value={`${
          chareableWeight?.boxWiseArr && chareableWeight?.boxWiseArr?.[Number(id)]?.maxVolumetricWeight
            ? selectedUnit === 'lb'
              ? (chareableWeight.boxWiseArr?.[Number(id)].maxVolumetricWeight * 2.20462).toFixed(2)
              : chareableWeight.boxWiseArr?.[Number(id)].maxVolumetricWeight.toFixed(2)
            : (0).toFixed(2)
        } ${selectedUnit}`}
      />
      <BoxInfo
        label="Chargeable Weight"
        value={`${
          chareableWeight?.boxWiseArr
            ? `${
                selectedUnit === 'lb'
                  ? customWeightRounding((chareableWeight?.boxWiseArr[Number(id)]?.minChargeableWeight ?? 0) * 2.20462)
                  : Number(chareableWeight?.boxWiseArr?.[Number(id)]?.minChargeableWeight ?? 0).toFixed(2)
              } - ${
                selectedUnit === 'lb'
                  ? customWeightRounding((chareableWeight?.boxWiseArr[Number(id)]?.maxChargeableWeight ?? 0) * 2.20462)
                  : Number(chareableWeight?.boxWiseArr?.[Number(id)]?.maxChargeableWeight ?? 0).toFixed(2)
              }`
            : (0).toFixed(2)
        } ${selectedUnit}`}
      />
      <BoxInfo label="Dimensions (L×W×H)" value={`${length} × ${width} × ${height} ${selectedLengthUnit}`} />
    </BoxContainer>
  );
}

export default SingleBoxView;
